import { Button, Input } from '@shared/ui/kit';
import * as stylex from '@stylexjs/stylex';
import { styles } from '../lib/styles';
import { FC, useState, useEffect } from 'react';
import {
  ILoginVariables,
  passwordValidator,
  emailValidator,
  IValidateValue,
  REQURED_MAIL_MESSAGE,
  REQURED_PASSWORD_MESSAGE,
} from '../lib/auth-validator';
import { YandexCaptcha } from '../captcha/yandex-captcha';

const isProduction = import.meta.env.VITE_ENV_TYPE === 'prod';

interface LoginFormProps {
  onSubmit: ({ email, password, captcha_token }: ILoginVariables) => void;
  errorMessage?: string;
  onChange?: () => void;
  isLoading?: boolean;
}

export const LoginForm: FC<LoginFormProps> = ({ onSubmit, errorMessage, onChange, isLoading = false }) => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [emailErrors, setEmailErrors] = useState<IValidateValue>(false);
  const [passwordErrors, setPasswordErrors] = useState<IValidateValue>(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [resetCaptcha, setResetCaptcha] = useState(0);

  useEffect(() => {
    if (email) {
      emailValidator(email).then(setEmailErrors);
    }
  }, [email]);

  useEffect(() => {
    if (password) {
      passwordValidator(password).then(setPasswordErrors);
    }
  }, [password]);

  useEffect(() => {
    if (captchaToken && !errorMessage && !isLoading) {
      onSubmit({ email, password, captcha_token: captchaToken });
    }
  }, [captchaToken]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) {
      setEmailErrors(REQURED_MAIL_MESSAGE);
    }
    if (!password) {
      setPasswordErrors(REQURED_PASSWORD_MESSAGE);
    }
    if (email && password && !emailErrors && !passwordErrors) {
      if (!isProduction) {
        onSubmit({ email, password, captcha_token: '' });
        return;
      }
      setResetCaptcha((prev) => prev + 1);
      setShowCaptcha(true);
    }
  };

  const handleChangeEmail = (event: React.SetStateAction<string>) => {
    setEmail(event);
    if (onChange) onChange();
  };

  const handleChangePassword = (event: React.SetStateAction<string>) => {
    setPassword(event);
    if (onChange) onChange();
  };

  if (isLoading) {
    return (
      <form {...stylex.props(styles.form)}>
        <div {...stylex.props(styles.skeletonTitle)} />
        <div {...stylex.props(styles.skeleton)} />
        <div {...stylex.props(styles.skeleton)} />
        <div {...stylex.props(styles.skeletonButton)} />
        <div {...stylex.props(styles.errorMessage)} />
      </form>
    );
  }

  return (
    <form {...stylex.props(styles.form)} onSubmit={handleSubmit}>
      <div {...stylex.props(styles.title)}>Авторизация</div>
      <Input
        settings={{ width: '320px' }}
        inputMessage={emailErrors || ''}
        status={emailErrors ? 'error' : undefined}
        labelText="Почта *"
        changeFunction={handleChangeEmail}
        value={email}
        placeholder="Введите почту"
      />
      <Input
        settings={{ width: '320px' }}
        type="password"
        inputMessage={passwordErrors || ''}
        status={passwordErrors ? 'error' : undefined}
        labelText="Пароль *"
        changeFunction={handleChangePassword}
        value={password}
        placeholder="Введите пароль"
      />
      {isProduction && (
        <YandexCaptcha
          key={resetCaptcha}
          sitekey={import.meta.env.VITE_YC_CAPTCHA_SITEKEY}
          visible={showCaptcha}
          onSuccess={(token) => setCaptchaToken(token)}
          onChallengeHidden={() => setShowCaptcha(false)}
        />
      )}

      <Button variant="accent" settings={{ width: '100px' }}>
        <div style={{ color: '#fff' }}>Войти</div>
      </Button>

      <p {...stylex.props(styles.errorMessage)}>{errorMessage ? 'Ошибка аутентификации' : ''}</p>
    </form>
  );
};
